/* eslint-disable @typescript-eslint/consistent-type-definitions */
/** Rückgabeart der Cookies */
export type CookieType = string | Record<string, string>

export type CookieOptions = {
    /**
     * Das Datum bis wann der Cookie gültig sein soll.
     *
     * @type {Date}
     * @example
     * new Date()
     */
    expires?: Date

    /**
     * Das maximale Cookie alter in Sekunden.
     *
     * @type {number}
     * @example
     * // für 1 Jahr
     * 60*60*24*365
     * 31536000
     */
    maxAge?: number

    /**
     * Der Pfad oder die momentane document.location auf welche der Cookie gültig ist.
     *
     * @type {string}
     * @example
     * "/" // gültig auf "/", "/login", "/impressum", ...
     * "/login" // gültig auf "/login", "/login/forward", ...
     */
    path?: string

    /**
     * Die Domain auf welcher der Cookie gültig ist.
     *
     * @type {string}
     * @example
     * "example.com"
     * "subdomain.example.com"
     */
    domain?: string
}

export type DeleteCookieOptions = Pick<CookieOptions, "domain" | "path">

/**
 * Helferklasse, enthält Methoden zum Arbeiten mit Cookies.
 *
 * @export
 * @class CookieService
 */
export default class CookieUtils {
    /**
     * Setzt einen Document Cookie mit dem übergebenen Namen auf den jeweiligen übergebenen Wert.
     * Es können Optionen für den Cookie übergeben werden, diese sind aber optional. Falls der Parameter name nicht gesetzt wird, fängt die Methode
     * diesen ab und gibt den Fehler aus, dass keine leeren Strings als name des value gesetzt werden dürfen.
     *
     * Der aufruf `CookieUtils.set("login", "my-name")` würde einen Document Cookie setzen (`login=my-name`),
     * dieser Cookie würde bis zum Ende der Session halten, da weder `maxAge` noch `expires` in den Optionen angegeben wurde.
     * Gültig wäre der Cookie auf dem aktuellen Pfad der Seite, also wenn der Cookie auf `mywebsite.com/login` gesetzt wurde,
     * ist der Cookie ausschließlich auf `/login` und dessen unterseiten verfügbar. Um einen Cookie auf jedem Pfad einer
     * Domain verfügbar zu machen, müsste man in den Optionen den `path` auf `"/"` setzen. Die standarddomain (falls keine übergeben)
     * ist die aktuelle Domain.
     *
     * @static
     * @param {string} name key des cookies
     * @param {(string | boolean | number)} value wert des cookies
     * @param {CookieOptions} [options={}] die Optionen wie expires, path, domain & max-age
     * @memberof CookieUtils
     * @throws message if string is empty
     * @example
     * CookieUtils.set("login", "my-name", { path: "/", maxAge: 300 })
     */
    public static set(name: string, value: string | boolean | number, options: CookieOptions = {}): void {
        let expires = ""
        let maxAge = ""
        let path = ""
        let domain = ""

        if (!name) {
            throw new Error(`Der Name des Cookies darf keine falsy (null, "", undefined) values übergeben bekommen!`)
        }

        if (options.expires) {
            expires = `expires=${options.expires.toUTCString()};`
        }

        if (options.maxAge !== null && options.maxAge !== undefined) {
            maxAge = `max-age=${options.maxAge};`
        }

        if (options.path) {
            path = `path=${options.path};`
        }

        if (options.domain) {
            domain = `domain=${options.domain};`
        }

        document.cookie = `${name}=${value}; ${expires}${maxAge}${path}${domain}`
    }

    /**
     * Gibt einen boolschen Wert zurück. Falls der Cookie gefunden wurde, wird ein true returned. Falls nicht, wird ein false zurückgegeben.
     * Die Methode Get wird ausgeführt. Falls diese einen Null-Wert zurückgibt, ist der gesuchte Cookie nicht vorhanden. Bei einem Ergebnis
     * der Get-Methode wird dieses gegen null abgefragt und ein true wird zurückgegeben.
     *
     * @static
     * @param {string} name
     * @returns {boolean}
     * @memberof CookieUtils
     *
     * @example
     * CookieUtils.exists("hello")
     */
    public static exists(name: string): boolean {
        return !!name && !!CookieUtils.get(name)
    }

    /**
     * Gibt den Wert des übergebenen Document Cookie zurück, oder alle, falls kein parameter gesetzt wurde.
     * Wenn ein Name angegeben war und nur ein Cookie gefunden wurde, wird nur der Wert des Cookies zurückgegeben:
     * z.B. get("hello") würde bei einem Cookie ="hello=world" nur den string "world" zurückgeben. Wenn aber der parameter
     * name nicht übergeben wurde, oder mehrere Ergebnisse im Array gelanden sind, wird ein Object zurückgegeben welches
     * den Key Value Paaren entspricht, z.B: get() würde bei einem Cookie ="hello=world; foo=bar" das Object { hello: "world", foo: "bar" }
     * zurückgeben. Falls keine Werte gefunden werden wird null zurückgegeben.
     *
     * @static
     * @param {string} name Der Name des cookies nach dem gefragt wird.
     * @returns {string | { [key: string]: string } | null} Das geparste Objekt oder null.
     * @memberof CookieService
     */
    public static get(name?: string): CookieType | null {
        return CookieUtils.parseCookie(document.cookie, name)
    }

    /**
     * Löscht einen Cookie. Dabei wird der Parameter `max-age` auf einen ungültigen Wert gesetzt.
     *
     * @static
     * @param {string} name Der Name des Cookies, der gelöscht werden soll.
     * @param {DeleteCookieOptions} [options={}] die Optionen wie expires, path, domain & max-age
     * @memberof CookieService
     *
     * @example
     * CookieUtils.delete("name", { domain: "my.domain.com", path: "/" })
     */
    public static delete(name: string, { domain, path = "/" }: DeleteCookieOptions = {}): void {
        const optionStrings = ["max-age=-1", `path=${path}`]

        if (domain) {
            optionStrings.push(`domain=${domain}`)
        }

        const deleteCookie = `${name}=; ${optionStrings.join(";")};`
        document.cookie = deleteCookie
    }

    /**
     * Entfernt alle Cookies. Zuerst werden alle Cookies mit der Get-Methode ausgelesen. Danach wird jeder Cookie mit der
     * Delete-Methode einzeln gelöscht.
     *
     * @static
     * @memberof CookieService
     *
     * @example
     * CookieUtils.clear()
     */
    public static clear(): void {
        const cookies: CookieType | null = CookieUtils.get()
        if (cookies !== null) {
            Object.keys(cookies).forEach((key) => CookieUtils.delete(key))
        }
    }

    /**
     * Parsed den übergebenen Cookie String nach allen Key Value paaren und gibt diese zurück. Wenn ein Name übergeben wird
     * dann wird explizit nach diesem Key gesucht und nur die Value von diesem Key zurückgegeben.
     * Wenn ein Name angegeben war und nur ein Cookie gefunden wurde, wird nur der Wert des Cookies zurückgegeben:
     * z.B. get("hello") würde bei einem Cookie ="hello=world" nur den string "world" zurückgeben. Wenn aber der parameter
     * name nicht übergeben wurde, oder mehrere Ergebnisse im Array gelanden sind, wird ein Object zurückgegeben welches
     * den Key Value Paaren entspricht, z.B: get() würde bei einem Cookie ="hello=world; foo=bar" das Object { hello: "world", foo: "bar" }
     * zurückgeben. Falls keine Werte gefunden werden oder der übergebene Cookie eine Falsy value ergibt wird null zurückgegeben.
     *
     * @static
     * @param {string} cookie Der Cookie der geparsed werden soll.
     * @param {string} [name] Der Name des cookies nach dem gefragt wird.
     * @returns {string | { [key: string]: string } | null} Das geparste Objekt oder null.
     * @memberof CookieService
     */
    public static parseCookie(cookie: string, name?: string): CookieType | null {
        if (!cookie || name === "") {
            return null
        }

        let result: Record<string, string> = {}

        cookie.split(";").forEach((value: string) => {
            result = { ...result, ...CookieUtils.createObjectFromArray(value.split("="), name) }
        })

        const keys = Object.getOwnPropertyNames(result)
        const length = keys.length

        if (length === 0) {
            return null
        } else if (length === 1 && name) {
            return result[keys[0]]
        }

        return result
    }

    private static createObjectFromArray(array: string[], name?: string) {
        if (array.length === 2) {
            const key = array[0].trim()
            if (name) {
                return key === name ? { [key]: array[1].trim() } : {}
            } else {
                return { [key]: array[1].trim() }
            }
        } else {
            return {}
        }
    }
}
